import styled from 'styled-components';
import React from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams } from 'react-router';

import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getIsAuthed } from '@utils/selectors';
import { ROUTES } from '@constants/routes';
import { isCurrentUserBrokerageUser, useCurrentUser, usePermissionedModes } from '@hooks/user/useCurrentUser';
import { toCapitalCase } from '@utils/usefulFuncs';
import { clearLastIntent, getLastIntent, setLastIntent } from '@utils/intents';

import { Header } from '@app/navigation/header';
import { Body, Body1, Container, Page } from '@ui';
import { Loading } from '@ui/Loading';

import SignIn from '@pages/SignIn';
import SignUp from '@pages/SignUp';
import ForgotPassword from '@pages/ForgotPassword';
import ResetPassword from '@pages/ResetPassword';
import Splash from '@pages/splash';
import ToolSelection from '@pages/ToolSelection';
import DigitalAnalyst from '@pages/DigitalAnalyst';
import ClosedBeta from '@pages/ClosedBeta';
import CompanySnapshot from '@pages/CompanySnapshot';
import Screener from '@pages/Screener';
import { ThemeBuilder } from '@pages/ThemeBuilder';
import DocumentManagement from '@pages/DocumentManagement';
import DocumentVault from '@pages/DocumentVault';
import DocUploadTriggersPage from '@pages/DocUploadTriggersPage';
import StockDetail from '@pages/StockDetail';
import { DocumentViewer } from '@pages/DocumentViewer';
import EarningsCallSummaries from '@pages/EarningsCallSummaries';
import ResearchScreener from '@pages/PremiumScreener';
import ResearchDigitalAnalyst from '@pages/ResearchDigitalAnalyst';
import FactorVisualization from '@pages/FactorVisualization';
import FactorVisualizationChart from '@pages/FactorVisualizationChart';
import FedDigitalAnalyst from '@pages/FedDigitalAnalyst';
import FedGpt from '@pages/Fedgpt';
import { DocumentSummary } from '@pages/DocumentSummary';
import { LatestUpdatesAndNews } from '@pages/LatestUpdatesAndNews';
import WebpageUploader from '@pages/WebpageUploader';
import WarningIndicators from '@pages/WarningIndicators';
import BullBearCases from '@pages/BullBearCases';
import FedSentiment from '@pages/FedSentiment';
import WebDigitalAnalyst from '@pages/WebDigitalAnalyst';
import EvalTemplate from '@pages/EvalTemplate';
import CustomSnapshot from '@pages/CustomSnapshot';
import MacroEconomicSignals from '@pages/MacroEconomicSignals';
import EconomicImpact from '@pages/EconomicImpact';
import FedTableAnalysis from '@pages/TableAnalysis/FedTableAnalysis';
import { TextSearch } from '@pages/TextSearch';
import FAQ from '@pages/FAQ';
import LenderEarningCallAnalysis from '@pages/LenderEarningCallAnalysis';
import { AwaitDocProcessingToDigitalAnalystMode } from '@pages/AwaitDocProcessingToDigitalAnalystMode';
import { AccountSettings } from '@pages/AccountSettings';
import { ComplianceMode } from '@pages/ComplianceMode';
import FedRecentDocs from '@pages/FedRecentDocs';
import CreditAgreementAnalysis from '@pages/TableAnalysis/CreditAgreementAnalysis';
import Watchlist from '@pages/Watchlist';
import GenericTableAnalysis from '@pages/TableAnalysis/GenericTableAnalysis';
import PPMTableAnalysis from '@pages/TableAnalysis/PPMTableAnalysis';
import AccountInReviewPage from '@pages/AccountInReviewPage';
import { QueryHistory } from '@pages/QueryHistory';
import LiquidityTermsTableAnalysis from '@pages/TableAnalysis/LiquidityTermsTableAnalysis';
import { logger } from '@utils/usefulFuncs';
import GrowthEquityAnalysis from '@pages/GrowthEquityAnalysis';
import ClientTableAnalysis from '@pages/ClientTableAnalysis';
import ClientPeerTableAnalysis from '@pages/ClientPeerTableAnalysis';
import ClientDigitalAnalyst from '@pages/ClientDigitalAnalyst';
import CitationsGenerator from '@pages/CitationsGenerator';
import AgentModePage from '@pages/AgentModePage';
import { FlowAgentScript } from '@pages/FlowAgentScript';
import { AgentScriptGenerator } from '@features/agentScripting';
import ESGUpdatesPage from '@pages/ESGUpdatesPage';
import DocumentCreatorPage from '@pages/DocumentCreatorPage';
import { Sandbox } from '@pages/Sandbox';
import { ReasoningQuery } from '@pages/ReasoningQuery';
import { QuerySeries } from '@pages/QuerySeries';
import { DataAnalystAgent } from '@pages/DataAnalystAgent';
import { ScoreBackfill } from '@pages/ScoreBackfill';
import { FeedView } from '@pages/FeedView';
import { ScoreSpec } from '@pages/ScoreSpec';
import { EditScoreSpec } from '@pages/EditScoreSpec';
import { CreateScoreSpec } from '@pages/CreateScoreSpec';
import { FetchScoreSpec } from '@pages/FetchScoreSpec';
import About from '@pages/About';
import { SelectCaseStudy } from '@pages/SelectCaseStudy';
import RfpResponseQuery from '@pages/RfpResponseQuery';
import RFPEditPage from '@pages/RFPEditPage';
import QuickLook from '@pages/QuickLook';
import QuarterlyDataTemplate from '@pages/QuarterlyDataTemplate';
import ChatDigitalAnalyst from '@pages/ChatDigitalAnalyst';
import AgentChatDigitalAnalyst from '@pages/AgentChatDigitalAnalyst';
import EvestQueryPage from '@pages/EvestQueryPage';
import QueryWithAgentScriptsTool from '@containers/DigitalAnalyst/QueryWithAgentScripts';
import PeerBenchmarkingPage from '@pages/PeerBenchmarkingPage';
import TemplateRedownload from '@containers/DigitalAnalyst/TemplateRedownload';
import RFPChatDigitalAnalyst from '@pages/RFPChatDigitalAnalyst';
import { DOCUMENT_TEMPLATE_MODE_IDS } from '@constants';
import EarningsGridTemplate from '@pages/EarningsGridTemplate';
import Blog from '@pages/Blog';
import BlogArticle from '@pages/BlogArticle';
import BrokerageMenu from '@pages/BrokerageMenu';
import BrokerageTaxDocuments from '@pages/BrokerageTaxDocuments';
import AccountInitError from '@pages/AccountInitError';
import TermsOfServicePage from '@pages/App/TermsOfService';
import PrivacyPolicyPage from '@pages/App/PrivacyPolicy';
import PortfolioMonitoringPage from '@pages/PortfolioMonitoringPage';
import FourOhFourPage from '@pages/FourOhFourPage';
import AgentSpreadsheet from '@pages/AgentSpreadsheet';
import TableBuilderTool from '@pages/TableBuilderTool';
import AgentMemoryAdmin from '@pages/AgentMemoryAdmin';
import TagDetail from '@pages/TagDetail';
import AgentResultViewer from '@pages/AgentResultViewer';
import { RfpTemplateProcessor } from '@pages/RfpTemplateProcessor';
import { DocumentTemplateProcessorPage } from '@/pages/DocumentTemplateProcessorPage';
import { ModelDataCapture } from '@pages/ModelDataCapture';
import DocGridAgentTableAnalysis from '@/pages/TableAnalysis/DocGridAgentTableAnalysis';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const buildRouteElement = (name, extraParams = {}) => {
  if (name === 'FedGpt') {
    return <FedGpt />;
  }
  if (name === 'DocumentVault') {
    return <DocumentVault />;
  }
  if (name === 'FedSentiment') {
    return <FedSentiment />;
  }
  if (name === 'FedReleaseAnalysis') {
    return <FedTableAnalysis />;
  }
  if (name === 'FinancialStatementTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'NewDealTriage') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'GrowthEquityMemoTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'InvestmentMemoTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'NewDealEvaluation') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'QuickLook') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'PortfolioSnapshotTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'DDQTemplate') {
    return <RfpTemplateProcessor />;
  }

  return null;
};

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Redirect = ({ nonAuth }) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const intent = window.location.pathname + window.location.search;
    logger('intent', intent);
    setLastIntent(intent);
    navigate(nonAuth ? ROUTES.login : ROUTES.home);
  }, []);
  return null;
};

const ResetPasswordActiveSessionRedirect = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate('/app/users/edit?tab=preferences');
  }, []);
  return null;
};

// Score Spec moved from /score-spec to /advanced-score-spec. The backend's permissioned_modes
// still hands out the old path, so keep it resolving — carrying the sub-path across so deep links
// (/score-spec/specs/:id) land on the same spec rather than the list.
const LegacyScoreSpecRedirect = () => {
  const navigate = useNavigate();
  const params = useParams();
  const subPath = params['*'];

  React.useEffect(() => {
    navigate(subPath ? `${ROUTES.scoreSpec}/${subPath}` : ROUTES.scoreSpec, { replace: true });
  }, []);
  return null;
};

function routeName(s) {
  return s
    .split('/')
    .join('')
    .split('-')
    .map((s) => toCapitalCase(s))
    .join('');
}

const LoggedInRedirect = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const prevIntent = getLastIntent();
    const route = prevIntent || ROUTES.home;
    logger('navigating after signin', { prevIntent, route });
    navigate(route);
    clearLastIntent();
  }, []);

  return null;
};

const LoadingApp = () => {
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setError(true);
    }, 5000);

    return () => clearTimeout(timeout);
  });

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={'800px'}>
          {error ? (
            <Container bottom={140} top={16}>
              <Body1 bold>Something went wrong, refresh and try again.</Body1>
            </Container>
          ) : (
            <Loading />
          )}
        </Page>
      </Body>
    </PageWrapper>
  );
};

function AuthedRoutes() {
  const permissionedModes = usePermissionedModes();
  const currentUser = useCurrentUser();
  const isBrokerageUser = isCurrentUserBrokerageUser(currentUser);

  // const isAccountDeleted = isCurrentUserAccountDeleted(currentUser);
  // if (isAccountDeleted) {
  //   return (
  //     <SentryRoutes>
  //       <Route path="/*" element={<BrokerageTaxDocuments />} />
  //     </SentryRoutes>
  //   );
  // }
  if (!currentUser) {
    return (
      <SentryRoutes>
        <Route path="/*" element={<LoadingApp />} />
      </SentryRoutes>
    );
  }
  if ((permissionedModes.length === 0 && isBrokerageUser) || isBrokerageUser) {
    return (
      <SentryRoutes>
        <Route path={ROUTES.brokerageTaxDocuments} element={<BrokerageTaxDocuments />} />
        <Route path="/*" element={<BrokerageMenu />} />
      </SentryRoutes>
    );
  }
  if (permissionedModes.length === 0) {
    return (
      <SentryRoutes>
        <Route path="/*" element={<AccountInitError />} />
      </SentryRoutes>
    );
  }
  return (
    <SentryRoutes>
      <Route path={ROUTES.brokerageTaxDocuments} element={<BrokerageTaxDocuments />} />
      <Route path={ROUTES.brokerageAccount} element={<BrokerageMenu />} />
      <Route path={ROUTES.digitalAnalyst} element={<DigitalAnalyst />} />
      <Route path={ROUTES.researchDigitalAnalyst} element={<ResearchDigitalAnalyst />} />
      <Route path={ROUTES.chatDigitalAnalyst} element={<ChatDigitalAnalyst />} />
      <Route path={ROUTES.agentChatDigitalAnalyst} element={<AgentChatDigitalAnalyst />} />
      <Route path={ROUTES.rfpResponseQuery} element={<RfpResponseQuery />} />
      <Route path={ROUTES.rfpChatDigitalAnalyst} element={<RFPChatDigitalAnalyst />} />
      <Route path={ROUTES.evestmentQuery} element={<EvestQueryPage />} />
      <Route path={ROUTES.rfpAdmin} element={<RFPEditPage />} />
      <Route path={ROUTES.fedLatestDocs} element={<FedRecentDocs />} />
      <Route path={ROUTES.fedDigitalAnalyst} element={<FedDigitalAnalyst />} />
      <Route path={ROUTES.website} element={<WebDigitalAnalyst />} />
      <Route path={ROUTES.lenderEarningsUpdates} element={<LenderEarningCallAnalysis />} />
      <Route path={ROUTES.companySnapshot} element={<CompanySnapshot />} />
      <Route path={ROUTES.customSnapshot} element={<CustomSnapshot />} />
      <Route path={ROUTES.warningIndicators} element={<WarningIndicators />} />
      <Route path={ROUTES.bullBearCases} element={<BullBearCases />} />
      <Route path={ROUTES.factor} element={<FactorVisualization />} />
      <Route path={ROUTES.factorVisual} element={<FactorVisualizationChart />} />
      <Route path={ROUTES.screener} element={<Screener />} />
      <Route path={ROUTES.researchScreener} element={<ResearchScreener />} />
      <Route path={ROUTES.latestEarningsUpdates} element={<EarningsCallSummaries />} />
      <Route path={ROUTES.latestUpdates} element={<LatestUpdatesAndNews />} />
      <Route path={ROUTES.stock} element={<StockDetail />} />
      <Route path={ROUTES.securityDetail} element={<StockDetail />} />
      <Route path={ROUTES.tagDetail} element={<TagDetail />} />
      <Route path={ROUTES.agentResult} element={<AgentResultViewer />} />
      <Route path={ROUTES.docSummary} element={<DocumentSummary />} />
      <Route path={ROUTES.watchlist} element={<Watchlist />} />
      <Route path={ROUTES.compliance} element={<ComplianceMode />} />
      <Route path={ROUTES.queryHistory} element={<QueryHistory />} />
      <Route path={ROUTES.docProcessingToDA} element={<AwaitDocProcessingToDigitalAnalystMode />} />
      <Route path={ROUTES.docViewer} element={<DocumentViewer />} />
      <Route path={ROUTES.docManagement} element={<DocumentManagement />} />
      <Route path={ROUTES.documentVault} element={<DocumentVault />} />
      <Route path={ROUTES.docUploadTriggers} element={<DocUploadTriggersPage />} />
      <Route path={ROUTES.webpageIngestion} element={<WebpageUploader />} />
      <Route path={ROUTES.textSearch} element={<TextSearch />} />
      <Route path={ROUTES.creditAgreementAnalysis} element={<CreditAgreementAnalysis />} />
      <Route path={ROUTES.liquidityTermsTableAnalysis} element={<LiquidityTermsTableAnalysis />} />
      <Route path={ROUTES.ppmTableAnalysis} element={<PPMTableAnalysis />} />
      <Route path={ROUTES.tableAnalysis} element={<GenericTableAnalysis />} />
      <Route path={ROUTES.macroEconomicSignals} element={<MacroEconomicSignals />} />
      <Route path={ROUTES.growthEquityAnalysis} element={<GrowthEquityAnalysis />} />
      <Route path={ROUTES.economicImpact} element={<EconomicImpact />} />
      <Route path={ROUTES.legacyQuickLook} element={<QuickLook />} />
      <Route path={ROUTES.evaluationTemplate} element={<EvalTemplate />} />
      <Route path={ROUTES.quarterlyDataTemplate} element={<QuarterlyDataTemplate />} />
      <Route path={ROUTES.peerBenchmarking} element={<PeerBenchmarkingPage />} />
      <Route path={ROUTES.earningsGridTemplate} element={<EarningsGridTemplate />} />
      <Route path={ROUTES.docGridAgent} element={<DocGridAgentTableAnalysis />} />
      <Route path={ROUTES.portfolioMonitoringTemplate} element={<PortfolioMonitoringPage />} />
      <Route path={ROUTES.accountSettings} element={<AccountSettings />} />
      <Route path={ROUTES.clientMatrix} element={<ClientTableAnalysis />} />
      <Route path={ROUTES.clientPeerMatrix} element={<ClientPeerTableAnalysis />} />
      <Route path={ROUTES.clientQuery} element={<ClientDigitalAnalyst />} />
      <Route path={ROUTES.home} element={<ToolSelection />} />
      <Route path={ROUTES.login} element={<LoggedInRedirect />} />
      <Route path={ROUTES.resetPassword} element={<ResetPasswordActiveSessionRedirect />} />
      <Route path={ROUTES.themeBuilder} element={<ThemeBuilder />} />
      <Route path={ROUTES.faq} element={<FAQ />} />
      <Route path={ROUTES.templateRedownload} element={<TemplateRedownload />} />
      <Route path={ROUTES.filledTemplateDownload} element={<TemplateRedownload />} />
      <Route path={ROUTES.tableBuilderTool} element={<TableBuilderTool />} />
      <Route path={ROUTES.agentSpreadsheet} element={<AgentSpreadsheet />} />
      <Route path={ROUTES.agentMemoryAdmin} element={<AgentMemoryAdmin />} />
      <Route
        path={ROUTES.newDealTriage}
        element={<DocumentCreatorPage modeId={DOCUMENT_TEMPLATE_MODE_IDS.new_deal_triage} />}
      />
      <Route
        path={ROUTES.newDealEvaluation}
        element={<DocumentCreatorPage modeId={DOCUMENT_TEMPLATE_MODE_IDS.new_deal_evaluation} />}
      />
      <Route path={ROUTES.inReview} element={<AccountInReviewPage />} />
      <Route path={ROUTES.esgUpdates} element={<ESGUpdatesPage />} />
      <Route path={ROUTES.citations} element={<CitationsGenerator />} />
      <Route path={ROUTES.agentScriptEditor} element={<AgentModePage />} />
      <Route path={ROUTES.agentScriptGenerator} element={<AgentScriptGenerator />} />
      <Route path={ROUTES.flowAgentScriptEditor} element={<FlowAgentScript />} />
      <Route path={ROUTES.queryAgentScript} element={<QueryWithAgentScriptsTool />} />
      <Route path={ROUTES.reasoningQuery} element={<ReasoningQuery />} />
      <Route path={ROUTES.querySeries} element={<QuerySeries />} />
      <Route path={ROUTES.dataAnalystAgent} element={<DataAnalystAgent />} />
      <Route path={`${ROUTES.scoreBackfill}/*`} element={<ScoreBackfill />} />
      <Route path={`${ROUTES.scoreSpec}/*`} element={<ScoreSpec />} />
      <Route path={`${ROUTES.legacyScoreSpec}/*`} element={<LegacyScoreSpecRedirect />} />
      <Route path={ROUTES.editScoreSpec} element={<EditScoreSpec />} />
      {/* The fetch screen's "Edit this spec" sends an approved record here preselected, so step
          one is not asked twice. Same page — the id in the path is the whole difference. */}
      <Route path={`${ROUTES.editScoreSpec}/:specId`} element={<EditScoreSpec />} />
      <Route path={ROUTES.createScoreSpec} element={<CreateScoreSpec />} />
      <Route path={ROUTES.getScoreSpec} element={<FetchScoreSpec />} />
      {/* The specs list opens a clicked row here rather than in the advanced editor. Same page,
          same component — the id in the path is what turns the picker into a deep link. */}
      <Route path={`${ROUTES.getScoreSpec}/:specId`} element={<FetchScoreSpec />} />
      <Route path={`${ROUTES.feedView}/*`} element={<FeedView />} />
      {/* Model Data Capture owns runs/:runId, the review queue, catalog, audit and settings as
          nested routes, so it is a splat like Score Backfill above. */}
      <Route path={`${ROUTES.fmdc}/*`} element={<ModelDataCapture />} />

      <Route path={ROUTES.ddqTemplate} element={<RfpTemplateProcessor />} />
      <Route path={ROUTES.documentTemplateProcessor} element={<DocumentTemplateProcessorPage />} />

      {permissionedModes.map((m) => {
        const { route, mode_type } = m;
        if (!route) return null;
        // Score Backfill is hardcoded above as `/score-backfill/*` so the page can own nested
        // sub-routes (history, jobs/:jobId). Skip the generic duplicate: an exact `/score-backfill`
        // here would outrank the splat and render a blank element on the bare path.
        if (route === ROUTES.scoreBackfill) return null;
        // Score Spec is likewise hardcoded above as `/advanced-score-spec/*` to own its nested
        // list/editor sub-routes — skip the generic duplicate so it doesn't shadow the splat. The
        // legacy `/score-spec` value is what the backend still sends; skipping it too leaves the
        // redirect above in charge instead of a mapped copy that renders nothing.
        if (route === ROUTES.scoreSpec || route === ROUTES.legacyScoreSpec) return null;
        // Feed View is hardcoded above as `/feed-view/*` so the page can own the per-component
        // drill-down sub-routes it's designed for; skip the generic duplicate for the same reason.
        if (route === ROUTES.feedView) return null;
        // Model Data Capture is hardcoded above as `/model-data-capture/*` for the same reason; an
        // exact mapped copy here would outrank the splat and render a blank element.
        if (route === ROUTES.fmdc) return null;
        // Document Vault is hardcoded above; skip the generic duplicate so route resolution
        // can't land on the mapped copy. `buildRouteElement` also handles the name as a
        // second line of defense.
        if (route === ROUTES.documentVault) return null;
        const RouteName = routeName(route);
        return <Route path={route} element={buildRouteElement(RouteName, { modeId: mode_type })} key={route} />;
      })}

      <Route path={'/sandbox'} element={<Sandbox />} />
      <Route path="/closed-beta" element={<ClosedBeta />} />
      <Route path={ROUTES.selectCaseStudy} exact element={<SelectCaseStudy />} />
      <Route path={'/blog/:pid'} element={<BlogArticle />} />
      <Route path={ROUTES.blog} element={<Blog />} />
      <Route path={ROUTES.termsOfService} exact element={<TermsOfServicePage />} />
      <Route path={ROUTES.privacyPolicy} exact element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.termsOfServiceLegacy} exact element={<TermsOfServicePage />} />
      <Route path={ROUTES.privacyPolicyLegacy} exact element={<PrivacyPolicyPage />} />
      <Route path="/*" element={<Redirect />} />
    </SentryRoutes>
  );
}

function NonAuthedRoutes() {
  return (
    <SentryRoutes>
      <Route path={ROUTES.faq} element={<FAQ />} />
      <Route path={ROUTES.about} element={<About />} />

      <Route path={ROUTES.selectCaseStudy} exact element={<SelectCaseStudy />} />
      <Route path={ROUTES.login} exact element={<SignIn isPasswordVisible={true} />} />
      <Route path={ROUTES.annalyLogin} exact element={<SignIn isPasswordVisible={false} />} />
      {/* ROUTES.sso intentionally not mounted — SAML/exchange endpoint not implemented backend-side.
          Re-enable in the SAML phase together with V2/V3 fixes from security-plan.md. */}
      <Route path={ROUTES.signup} exact element={<SignUp />} />
      <Route path={ROUTES.forgotPassword} exact element={<ForgotPassword />} />
      <Route path={ROUTES.resetPassword} exact element={<ResetPassword />} />
      <Route path={ROUTES.resetPasswordApp} exact element={<ResetPassword />} />
      <Route path={ROUTES.termsOfServiceLegacy} exact element={<TermsOfServicePage />} />
      <Route path={ROUTES.privacyPolicyLegacy} exact element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.termsOfService} exact element={<TermsOfServicePage />} />
      <Route path={ROUTES.privacyPolicy} exact element={<PrivacyPolicyPage />} />
      <Route path={ROUTES.splash} element={<Splash />} />
      <Route path={'/blog/:pid'} element={<BlogArticle />} />
      <Route path={ROUTES.blog} element={<Blog />} />
      <Route path="/*" element={<FourOhFourPage />} />
    </SentryRoutes>
  );
}

export default function Router() {
  const isAuthed = useSelector(getIsAuthed);

  if (isAuthed) {
    return <AuthedRoutes />;
  }
  return <NonAuthedRoutes />;
}
